import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteDashboardProfile, updateDashboardProfile } from "@dashboard/api/dashboardProfileAPI";
import { dashboardProfileOptions, dashboardProfilesQueryKey } from "@dashboard/api/queryApi";
import type {
    DashboardProfile,
    DashboardProfileConfig,
    DashboardProfileMetadata,
} from "@dashboard/utils/dashboard.types";
import { useSidebarRefresh } from "@/features/hub/sidebarRefreshContext";
import { useDashboardProfilesQuery } from "./useDashboardProfilesQuery";

export function useDashboardProfiles(
    productInstanceId: number,
    initialProfileName: string,
    selectedProfileId?: number,
) {
    const queryClient = useQueryClient();
    const sidebarRefresh = useSidebarRefresh();
    const profilesQueryKey = dashboardProfilesQueryKey(productInstanceId);
    const profilesQuery = useDashboardProfilesQuery(productInstanceId, initialProfileName);

    const profiles = profilesQuery.data ?? [];

    const resolvedProfileId = selectedProfileId ?? profiles[0]?.id ?? null;
    const selectedProfileQuery = useQuery(
        dashboardProfileOptions(productInstanceId, resolvedProfileId),
    );

    const saveMutation = useMutation({
        mutationFn: ({
            profileId,
            config,
        }: {
            profileId: number;
            config: DashboardProfileConfig;
        }) => updateDashboardProfile(productInstanceId, profileId, { config }),
        onSuccess: (profile) => {
            queryClient.setQueryData(
                dashboardProfileOptions(productInstanceId, profile.id).queryKey,
                profile,
            );
        },
    });

    const renameMutation = useMutation({
        mutationFn: ({ profileId, name }: { profileId: number; name: string }) =>
            updateDashboardProfile(productInstanceId, profileId, { name }),
        onSuccess: (profile) => {
            queryClient.setQueryData(
                dashboardProfileOptions(productInstanceId, profile.id).queryKey,
                profile,
            );
            queryClient.setQueryData<DashboardProfileMetadata[]>(profilesQueryKey, (profiles) =>
                profiles?.map((existingProfile) =>
                    existingProfile.id === profile.id ? toMetadata(profile) : existingProfile,
                ),
            );
            void queryClient.invalidateQueries({ queryKey: profilesQueryKey });
            void sidebarRefresh?.refreshSidebar();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (profileId: number) => deleteDashboardProfile(productInstanceId, profileId),
        onSuccess: (profile) => {
            queryClient.removeQueries({
                queryKey: dashboardProfileOptions(productInstanceId, profile.id).queryKey,
            });
            queryClient.setQueryData<DashboardProfileMetadata[]>(profilesQueryKey, (profiles) =>
                profiles?.filter((existingProfile) => existingProfile.id !== profile.id),
            );
            void queryClient.invalidateQueries({ queryKey: profilesQueryKey });
            void sidebarRefresh?.refreshSidebar();
        },
    });

    return {
        profiles,
        profile: selectedProfileQuery.data,
        isPending:
            profilesQuery.isPending ||
            (resolvedProfileId !== null && selectedProfileQuery.isPending),
        isError: profilesQuery.isError || selectedProfileQuery.isError,
        error: profilesQuery.error ?? selectedProfileQuery.error,
        saveProfile: saveMutation.mutateAsync,
        isSaving: saveMutation.isPending,
        renameProfile: renameMutation.mutateAsync,
        isRenaming: renameMutation.isPending,
        deleteProfile: deleteMutation.mutateAsync,
    };
}

function toMetadata(profile: DashboardProfile): DashboardProfileMetadata {
    return {
        id: profile.id,
        name: profile.name,
        createdAt: profile.createdAt,
        updatedAt: profile.updatedAt,
    };
}
